import { inject, Injectable } from '@angular/core'
import { NumericAbbreviations } from '@shared/constants/numeric-abbreviations'
import { RegExpEnum } from '@shared/enums/reg-exp.enum'
import { NumericAbbreviationInterface } from '@shared/interfaces/number-abbreviation.interface'
import { TranslateService } from '@ngx-translate/core'
import { SpecialSymbolsEnum } from '@shared/constants/special-symbols.enum'
import { MaxDecimalCount } from '@modules/stocks-module/general/constants/max-decimal-count'

@Injectable({
	providedIn: 'root',
})
export class TextTransformerService {
	protected readonly math: Math = Math
	protected readonly number: NumberConstructor = Number
	protected readonly regExp: RegExpConstructor = RegExp
	protected readonly regExpEnum: typeof RegExpEnum = RegExpEnum
	protected readonly numericAbbreviations: NumericAbbreviationInterface[] = NumericAbbreviations
	protected readonly specialSymbolsEnum: typeof SpecialSymbolsEnum = SpecialSymbolsEnum
	protected readonly maxDecimalCount: number = MaxDecimalCount

	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	// стандартное форматирование для всех чисел, запятая, и пробелы
	private _formatNumber(value: number | string): string {
		const [int = '', dec = '']: (string | undefined)[] = value
			.toLocaleString('en-EN', { maximumFractionDigits: this.maxDecimalCount })
			.replace(',', '')
			.split('.')
		const formattedInt: string = int.replace(new this.regExp(this.regExpEnum.THOUSANDS_SEPARATOR, 'g'), ' ')
		const spacedNumber: string = `${formattedInt}${dec.length ? `.${dec}` : ''}`
		const separatedNumber: string = spacedNumber.replace('.', ',')

		return separatedNumber
	}

	// Добавляет "..." при превышении длины
	public toEllipsisText(value: string | undefined, maxLength: number): string | null {
		if (!value) return null
		if (value?.length <= maxLength) return value

		return value.substr(0, maxLength) + '...'
	}

	// Форматирование чисел
	public toQuantity(value: number | string | null | undefined): string {
		const numberValue: number | undefined = this.number(value)
		if (this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash
		if (numberValue === 0) return '0'
		return this._formatNumber(numberValue)
	}

	// Форматирование цены
	public toPrice(value: number | string | null | undefined, withSymbol: boolean = true): string {
		const numberValue: number | undefined = this.number(value)
		if (this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash
		if (numberValue === 0) return `${withSymbol ? '$' : ''}0`
		const valueWithSymbol: string =
			numberValue > -1 && numberValue < 1
				? `${withSymbol ? '$' : ''}${this._formatNumber(numberValue.toFixed(4))}`
				: `${withSymbol ? '$' : ''}${this._formatNumber(numberValue.toFixed(2))}`

		const valueWithoutSign: string = valueWithSymbol.replace('-', '')

		return numberValue < 0 ? `${this.specialSymbolsEnum.EN_Dash}${valueWithoutSign}` : `${valueWithoutSign}`
	}

	// Форматирование суммы
	public toAmount(value: number | string | null | undefined, withSymbol: boolean = true): string {
		const numberValue: number | undefined = this.number(value)
		if (numberValue === undefined || this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash
		if (numberValue === 0) return `${withSymbol ? '$' : ''}0`

		const amount: string = this._formatNumber(numberValue.toFixed(2))

		return `${withSymbol ? '$' : ''}${amount}`
	}

	// Форматирование доходности доллара и процента
	public toProfit(value: number | string | null | undefined, type: 'dollar' | 'percent'): string {
		const numberValue: number | undefined = this.number(value)
		if (this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash

		const formattedValue: string = this._formatNumber(numberValue.toFixed(2))
		const characterValue: string = type === 'dollar' ? `$${formattedValue}` : `${formattedValue}%`

		const cleanValue: string = characterValue.replace('-', '')

		if (numberValue > 0) {
			return `+${cleanValue}`
		} else if (numberValue < 0) {
			return `${this.specialSymbolsEnum.EN_Dash}${cleanValue}`
		} else {
			return cleanValue
		}
	}

	// Форматирование числа в сокращённый вид
	public toCompactNumber(value: number | string | null | undefined): string {
		const numberValue: number | undefined = this.number(value)
		if (this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash

		let result: string = ''

		const bestMatch: NumericAbbreviationInterface | undefined = this.numericAbbreviations
			.filter((abbr: NumericAbbreviationInterface): boolean => this.math.abs(numberValue) >= abbr.value)
			.sort((a: NumericAbbreviationInterface, b: NumericAbbreviationInterface): number => b.value - a.value)[0]

		if (bestMatch) {
			let resultValue: string = (numberValue / bestMatch.value).toFixed(2)
			resultValue = resultValue.replace('.', ',')
			const suffix: string = this._translateService.instant(bestMatch.suffix)
			result = `${resultValue}${suffix}`
		} else {
			result = numberValue.toFixed(2)
		}

		return result
	}

	// Форматирование процентов
	public toPercent(value: number | string | null | undefined, isReduceMin: boolean = false): string {
		const numberValue: number | undefined = this.number(value)
		if (this.number.isNaN(numberValue)) return this.specialSymbolsEnum.EN_Dash
		const formattedNumber: string = this._formatNumber(numberValue.toFixed(2))

		let [int, dec]: (string | undefined)[] = formattedNumber.toString().split(',')
		int = int ?? '0'
		dec = ',' + (dec ? dec.slice(0, 2) : '00')
		const result: string = (int + dec).replace('-', this.specialSymbolsEnum.EN_Dash)

		if (numberValue === 0) return result + '%'
		return (isReduceMin && numberValue < 0.01 ? '< 0,01' : result) + '%'
	}
}
